<template>
  <div class="date-picker-inline" ref="container">
    <VueDatePicker
      v-model="date"
      locale="fr"
      ref="datePicker"
      :range="isRange"
      :inline="true"
      :autoApply="true"
      :enableTimePicker="/time/i.test(type)"
      :timePicker="type == 'time'"
      :weekPicker="type == 'week'"
      :yearPicker="type == 'year'"
      :monthPicker="type == 'month'"
      :minDate="new Date(minDate)"
      :multiCalendars="multiCalendars"
      :preventMinMaxNavigation="true"
      :vertical="false"
    >
    </VueDatePicker>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import '@vuepic/vue-datepicker/dist/main.css'
import VueDatePicker from '@vuepic/vue-datepicker'

export default defineComponent({
  name: 'DatePickerInline',
  components: {
    VueDatePicker,
  },
  props: {
    value: {
      type: [String, Number, Boolean, Object],
      default: '',
    },
    modelValue: {
      type: [String, Number, Boolean, Object],
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
    isRange: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: [Number, Date],
      default: Date.now(),
    },
    multiCalendars: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      date: this.value || this.modelValue || (this.isRange ? [] : Date.now()),
    }
  },
  mounted() {},
  methods: {},
  watch: {
    date: {
      deep: true,
      handler(newValue, old) {
        this.$emit('update:modelValue', newValue)
        this.$emit('input:modelValue', newValue)
        this.$emit('input', newValue)
      },
    },
    value: {
      deep: true,
      handler(newValue, old) {
        this.date = newValue || (this.isRange ? [] : null)
      },
    },
  },
  computed: {},
})
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>
:root {
  /*General*/
  /* --dp-font-family: -apple-system, blinkmacsystemfont, 'Segoe UI', roboto,
    oxygen, ubuntu, cantarell, 'Open Sans', 'Helvetica Neue', sans-serif; */
  /* --dp-font-family: 'Poppins'; */
  --dp-border-radius: 4px; /*Configurable border-radius*/
  --dp-cell-border-radius: 100000px; /*Specific border radius for the calendar cell*/
  --dp-common-transition: all 0.1s ease-in; /*Generic transition applied on buttons and calendar cells*/

  /*Sizing*/
  --dp-button-height: 35px; /*Size for buttons in overlays*/
  --dp-month-year-row-height: 35px; /*Height of the month-year select row*/
  --dp-month-year-row-button-size: 35px; /*Specific height for the next/previous buttons*/
  --dp-button-icon-height: 20px; /*Icon sizing in buttons*/
  --dp-cell-size: 2.8rem; /*Width and height of calendar cell*/
  --dp-cell-padding: 0.5rem; /*Padding in the cell*/
  --dp-common-padding: 10px; /*Common padding used*/
  --dp-input-icon-padding: 35px; /*Padding on the left side of the input if icon is present*/
  --dp-input-padding: 6px 30px 6px 12px; /*Padding in the input*/
  --dp-menu-min-width: 100%; /*Adjust the min width of the menu*/
  --dp-action-buttons-padding: 2px 5px; /*Adjust padding for the action buttons in action row*/
  --dp-row-margin: 5px 0; /*Adjust the spacing between rows in the calendar*/
  --dp-calendar-header-cell-padding: 0.5rem; /*Adjust padding in calendar header cells*/
  --dp-two-calendars-spacing: 6rem; /*Space between multiple calendars*/
  --dp-overlay-col-padding: 3px; /*Padding in the overlay column*/
  --dp-time-inc-dec-button-size: 5rem; /*Sizing for arrow buttons in the time picker*/
  --dp-menu-padding: 6px 8px; /*Menu padding*/

  /*Font sizes*/
  --dp-font-size: 1rem; /*Default font-size*/
  --dp-preview-font-size: 0.8rem; /*Font size of the date preview in the action row*/
  --dp-time-font-size: 3rem; /*Font size in the time picker*/

  /*Transitions*/
  --dp-animation-duration: 0.1s; /*Transition duration*/
  --dp-menu-appear-transition-timing: cubic-bezier(
    0.4,
    0,
    1,
    1
  ); /*Timing on menu appear animation*/
  --dp-transition-timing: ease-out; /*Timing on slide animations*/
}

/* Mobile compact styles */
@media (max-width: 768px) {
  :root {
    --dp-cell-size: 2.5rem;
    --dp-cell-padding: 0.15rem;
    --dp-button-height: 22px;
    --dp-month-year-row-height: 22px;
    --dp-month-year-row-button-size: 22px;
    --dp-button-icon-height: 14px;
    --dp-common-padding: 3px;
    --dp-row-margin: 1px 0;
    --dp-calendar-header-cell-padding: 0.2rem;
    --dp-two-calendars-spacing: 3rem;
    --dp-menu-padding: 3px 5px;
    --dp-font-size: 0.9rem;
    --dp-preview-font-size: 0.8rem;
  }
}

.dp__theme_light {
  --dp-background-color: #fff;
  --dp-text-color: #212121;
  --dp-hover-color: #f3f3f3;
  --dp-hover-text-color: #212121;
  --dp-hover-icon-color: #959595;
  --dp-primary-color: #222222;
  --dp-primary-disabled-color: #c3a7dc;
  --dp-primary-text-color: #f8f5f5;
  --dp-secondary-color: #c0c4cc;
  --dp-border-color: transparent;
  --dp-menu-border-color: transparent;
  --dp-border-color-hover: #aaaeb7;
  --dp-border-color-focus: #aaaeb7;
  --dp-disabled-color: #f6f6f6;
  --dp-scroll-bar-background: #f3f3f3;
  --dp-scroll-bar-color: #959595;
  --dp-success-color: #76d275;
  --dp-success-color-disabled: #a3d9b1;
  --dp-icon-color: #959595;
  --dp-danger-color: #ff6f60;
  --dp-marker-color: #ff6f60;
  --dp-tooltip-color: #fafafa;
  --dp-disabled-color-text: #8e8e8e;
  --dp-highlight-color: rgb(25 118 210 / 10%);
  --dp-range-between-dates-background-color: var(--dp-hover-color, #f3f3f3);
  --dp-range-between-dates-text-color: var(--dp-hover-text-color, #212121);
  --dp-range-between-border-color: var(--dp-hover-color, #f3f3f3);
}

.dp__calendar_header {
  font-weight: normal;
}

/* .dp__range_end:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: red;
} */
</style>
